import { useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { 
  CheckCircle2,
  Clock,
  Circle,
  Loader2,
  Euro,
  Lock,
  ArrowDown,
  Save,
  Calendar as CalendarIcon,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import DevisPopup174 from "./DevisPopup174";

const STEP_STATUS_CONFIG = {
  not_started: { label: "Non démarré", icon: Circle, color: "text-muted-foreground", bg: "bg-muted", border: "border-border" },
  in_progress: { label: "En cours", icon: Loader2, color: "text-blue-600", bg: "bg-blue-500/10", border: "border-blue-500/30" },
  waiting: { label: "En attente", icon: Clock, color: "text-amber-600", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  done: { label: "Terminé", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10", border: "border-emerald-500/30" }
};

const DATE_STEPS = ["date_demarrage_travaux", "travaux_realises", "cofrac"];
const PAYMENT_STEPS_174 = ["paye", "paye_174", "prime_cee", "prime_apa", "prime_mpr"];
const DEVIS_STEPS = ["devis_signe", "mpr_devis_signe", "174_devis_signe"];
const SUB_STATUS_STEPS = ["sav", "sav_174", "sav_mpr", "compte_mpr"];

const getDuration = (date1, date2) => {
  if (!date1 || !date2) return null;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2 - d1);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "le même jour";
  if (diffDays === 1) return "1 jour";
  if (diffDays < 7) return `${diffDays} jours`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} semaine${Math.floor(diffDays / 7) > 1 ? 's' : ''}`;
  return `${Math.floor(diffDays / 30)} mois`;
};

const formatShortDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatCurrency = (amount) => {
  if (!amount) return "0 €";
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
};

// Determine default devis amount for a step
function getDefaultDevisAmount(step, dossier, isCumule) {
  if (!isCumule) return dossier.montant_devis_174 || 0;
  const prog = step.programme;
  if (prog === "174") return dossier.montant_devis_174 || 0;
  if (prog === "mpr") return (dossier.total_aides || 0) - (dossier.montant_devis_174 || 0);
  // Fallback: use key-based detection
  if (step.key === "mpr_devis_signe") return (dossier.total_aides || 0) - (dossier.montant_devis_174 || 0);
  if (step.key === "174_devis_signe") return dossier.montant_devis_174 || 0;
  return dossier.montant_devis_174 || 0;
}

export default function Etapes174({ 
  dossier, 
  savingStep, 
  onStepStatusChange,
  onStepDateChange,
  onStepAmountChange,
  isCumule = false,
  onDevisAmountChange,
  onDevisValidated,
  API,
  dossierId,
  fetchDossier,
  isRestricted = false,
}) {
  const [showDevisPopup, setShowDevisPopup] = useState(false);
  const [pendingDevisStepKey, setPendingDevisStepKey] = useState(null);
  const [travauxJustSaved, setTravauxJustSaved] = useState(false);
  const [amountInputs, setAmountInputs] = useState(() => {
    const inputs = {};
    dossier.steps?.forEach(step => {
      if (step.amount) {
        inputs[step.key] = step.amount.toString();
      }
    });
    // Pre-fill devis steps with correct amounts
    dossier.steps?.forEach(step => {
      if (DEVIS_STEPS.includes(step.key)) {
        if (step.amount) {
          inputs[step.key] = step.amount.toString();
        } else {
          const defaultAmount = getDefaultDevisAmount(step, dossier, isCumule);
          if (defaultAmount > 0) inputs[step.key] = defaultAmount.toString();
        }
      }
    });
    return inputs;
  });

  const [dateInputs, setDateInputs] = useState(() => {
    const dates = {};
    dossier.steps?.forEach(step => {
      if (step.completion_date) dates[step.key] = new Date(step.completion_date);
    });
    return dates;
  });

  const canModifyStep = (stepIndex) => {
    if (stepIndex === 0) return true;
    const prevStep = dossier.steps[stepIndex - 1];
    return prevStep?.status === 'done';
  };

  const handleAmountChange = (stepKey, value) => {
    setAmountInputs(prev => ({ ...prev, [stepKey]: value }));
  };

  const handleAmountSave = async (stepKey) => {
    const amount = parseFloat(amountInputs[stepKey]) || 0;
    
    // For devis steps, save the amount first, then open popup for travaux
    if (DEVIS_STEPS.includes(stepKey)) {
      if (!amount || amount <= 0) {
        toast.error("Veuillez saisir le montant du devis");
        return;
      }
      await onStepAmountChange(stepKey, amount);
      if (onDevisAmountChange) {
        const step = dossier.steps?.find(s => s.key === stepKey);
        onDevisAmountChange(stepKey, amount, step?.programme);
      }
      // Determine if this is a 174 devis step (open popup)
      const step = dossier.steps?.find(s => s.key === stepKey);
      const is174DevisStep = stepKey === "devis_signe" || (step?.programme === "174" && stepKey === "174_devis_signe");
      if (is174DevisStep) {
        setPendingDevisStepKey(stepKey);
        setShowDevisPopup(true);
      }
      return;
    }
    
    await onStepAmountChange(stepKey, amount);
  };

  const handleDateChange = async (stepKey, date) => {
    setDateInputs(prev => ({ ...prev, [stepKey]: date }));
    const dateStr = date ? format(date, 'yyyy-MM-dd') : null;
    await onStepDateChange(stepKey, dateStr);
  };

  const handleDevisPopupSave = async (travauxData, coutsData) => {
    try {
      if (API && dossierId) {
        await fetch(`${API}/dossiers/${dossierId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            travaux_devis: travauxData,
            couts_unitaires_devis: coutsData,
          }),
        });
      }
      // Auto-validate the devis step after saving travaux
      const stepKey = pendingDevisStepKey;
      const amount = parseFloat(amountInputs[stepKey]) || 0;
      if (stepKey && amount > 0) {
        await onStepStatusChange(stepKey, "done", { amount });
      }
      setShowDevisPopup(false);
      setPendingDevisStepKey(null);
      toast.success("Travaux enregistrés et étape validée");
      // Wait for backend to finish, then refresh
      setTimeout(() => { if (fetchDossier) fetchDossier(); }, 500);
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement des travaux");
    }
  };

  // Check if devis step can be validated to "done"
  const canValidateDevis = (stepKey) => {
    const step = dossier.steps?.find(s => s.key === stepKey);
    const is174DevisStep = stepKey === "devis_signe" || (step?.programme === "174" && stepKey === "174_devis_signe");
    
    if (is174DevisStep) {
      const hasAmount = step?.amount && step.amount > 0;
      const hasTravaux = dossier.travaux_devis && (dossier.travaux_devis.items || dossier.travaux_devis.logements);
      return hasAmount && hasTravaux;
    }
    return true;
  };

  // Intercept status change to validate devis requirements
  const handleStepStatusChangeWithValidation = (stepKey, newStatus) => {
    if (DEVIS_STEPS.includes(stepKey) && newStatus === "done") {
      const step = dossier.steps?.find(s => s.key === stepKey);
      
      // Check amount from step OR from local input
      const localAmount = parseFloat(amountInputs[stepKey]) || 0;
      const stepAmount = step?.amount || 0;
      if (stepAmount <= 0 && localAmount <= 0) {
        toast.error("Veuillez d'abord saisir et enregistrer le montant du devis");
        return;
      }
      
      // 174 devis steps also require travaux data (only for non-restricted users)
      const is174DevisStep = stepKey === "devis_signe" || (step?.programme === "174" && stepKey === "174_devis_signe");
      if (is174DevisStep && !isRestricted && !travauxJustSaved) {
        if (!dossier.travaux_devis || (!dossier.travaux_devis.items && !dossier.travaux_devis.logements)) {
          toast.error("Veuillez d'abord enregistrer le détail des travaux (cliquez sur Enregistrer le montant)");
          return;
        }
      }
      if (travauxJustSaved) setTravauxJustSaved(false);
      
      // If amount was entered locally but not yet saved to step, save it now
      if (stepAmount <= 0 && localAmount > 0) {
        onStepStatusChange(stepKey, newStatus, { amount: localAmount });
        return;
      }
    }
    onStepStatusChange(stepKey, newStatus);
  };

  // Detect programme sections for visual separation
  let currentProgramme = null;
  let sectionStarts = {};
  if (isCumule) {
    dossier.steps?.forEach((step, idx) => {
      const prog = step.programme || (idx < 10 ? "174" : "mpr");
      if (prog !== currentProgramme) {
        sectionStarts[idx] = prog;
        currentProgramme = prog;
      }
    });
  }

  return (
    <div className="border border-border rounded-sm bg-card">
      <div className="p-4 border-b border-border bg-muted/50">
        <h3 className="font-medium text-foreground flex items-center gap-2">
          <span className={cn("w-6 h-6 text-white rounded-full flex items-center justify-center text-xs font-bold", isCumule ? "bg-violet-600" : "bg-[hsl(145,100%,45%)]")}>
            {isCumule ? "C" : "174"}
          </span>
          {isCumule ? "Étapes Opération Cumulée (174 + MPR)" : "Étapes du programme 174 - Rénovation globale"}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {dossier.nombre_logements || 1} logement{(dossier.nombre_logements || 1) > 1 ? "s" : ""} 
          {dossier.nom_residence && ` - ${dossier.nom_residence}`}
        </p>
      </div>
      
      <div className="divide-y divide-border">
        {dossier.steps?.map((step, index) => {
          const stepStatusConfig = STEP_STATUS_CONFIG[step.status] || STEP_STATUS_CONFIG.not_started;
          const StepStatusIcon = stepStatusConfig.icon;
          const isSaving = savingStep === step.key;
          const isDateStep = DATE_STEPS.includes(step.key);
          const isPaymentStep = PAYMENT_STEPS_174.includes(step.key);
          const isDevisStep = DEVIS_STEPS.includes(step.key);
          const isLocked = !canModifyStep(index);
          const stepProgramme = step.programme;
          const is174Step = stepProgramme === "174";
          const isMprStep = stepProgramme === "mpr";
          
          const prevValidatedStep = index > 0 ? dossier.steps.slice(0, index).reverse().find(s => s.status === 'done' && s.updated_at) : null;
          const duration = prevValidatedStep && step.status === 'done' && step.updated_at
            ? getDuration(prevValidatedStep.updated_at, step.updated_at) : null;

          // Section header for cumule
          const sectionLabel = sectionStarts[index];
          
          return (
            <div key={step.key}>
              {/* Section separator for cumule */}
              {isCumule && sectionLabel && (
                <div className={cn(
                  "px-4 py-2 flex items-center gap-2 text-sm font-semibold",
                  sectionLabel === "174" 
                    ? "bg-emerald-500/10 text-emerald-800 border-b border-emerald-500/30" 
                    : "bg-blue-500/10 text-blue-800 border-b border-blue-500/30"
                )}>
                  <span className={cn(
                    "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white",
                    sectionLabel === "174" ? "bg-emerald-600" : "bg-blue-600"
                  )}>
                    {sectionLabel === "174" ? "174" : "M"}
                  </span>
                  {sectionLabel === "174" ? "Programme 174 - Rénovation globale" : "Programme MPR"}
                </div>
              )}
              
              <div 
                className={cn(
                  "p-4",
                  isLocked && "opacity-60",
                  isCumule && is174Step && "border-l-4 border-l-emerald-400",
                  isCumule && isMprStep && "border-l-4 border-l-blue-400"
                )}
                data-testid={`step-${step.key}`}
              >
                {duration && (
                  <div className="flex items-center gap-2 mb-2 ml-12 text-xs text-muted-foreground">
                    <ArrowDown className="w-3 h-3" />
                    <span className="bg-border px-2 py-0.5 rounded-sm">{duration}</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className={cn(
                      "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium",
                      step.status === 'done' ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" : 
                        isCumule && isMprStep ? "bg-blue-500/20 text-blue-600 dark:text-blue-400" : "bg-border text-[hsl(145,100%,39%)]"
                    )}>
                      {step.status === 'done' ? <CheckCircle2 className="w-4 h-4" /> : index + 1}
                    </div>
                    <div className={cn("w-10 h-10 rounded-sm flex items-center justify-center shrink-0", stepStatusConfig.bg)}>
                      {isLocked ? (
                        <Lock className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <StepStatusIcon className={cn("w-5 h-5", stepStatusConfig.color, step.status === 'in_progress' && "animate-spin")} />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">
                        {step.name}
                        {isCumule && stepProgramme && (
                          <span className={cn("ml-2 text-[10px] px-1.5 py-0.5 rounded font-semibold",
                            is174Step ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" : "bg-blue-500/20 text-blue-600 dark:text-blue-400"
                          )}>
                            {is174Step ? "174" : "MPR"}
                          </span>
                        )}
                      </p>
                      {step.completion_date && (
                        <p className="text-xs text-muted-foreground mt-1">Date : {formatShortDate(step.completion_date)}</p>
                      )}
                      {step.amount && isPaymentStep && (
                        <p className="text-xs text-emerald-600 mt-1 font-medium">Montant : {formatCurrency(step.amount)}</p>
                      )}
                      {step.updated_at && step.status === 'done' && (
                        <p className="text-xs text-emerald-600 mt-1">Validé le {formatShortDate(step.updated_at)}</p>
                      )}
                      {isLocked && (
                        <p className="text-xs text-amber-600 mt-1">Terminez d'abord l'étape précédente</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 ml-4 flex-wrap justify-end">
                    {isDevisStep && (
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="number"
                            placeholder="Montant devis"
                            value={amountInputs[step.key] || ""}
                            onChange={(e) => handleAmountChange(step.key, e.target.value)}
                            disabled={isLocked}
                            className="w-[140px] pl-9 rounded-sm text-right"
                            data-testid={`amount-input-${step.key}`}
                          />
                        </div>
                        <Button
                          variant="outline" size="sm"
                          onClick={() => handleAmountSave(step.key)}
                          disabled={isSaving || isLocked}
                          className="rounded-sm"
                          data-testid={`save-amount-${step.key}`}
                        >
                          <Save className="w-4 h-4" />
                        </Button>
                        {isCumule && (
                          <span className={cn("text-xs", is174Step ? "text-emerald-600" : "text-blue-600")}>
                            ({formatCurrency(getDefaultDevisAmount(step, dossier, isCumule))})
                          </span>
                        )}
                        {!isCumule && dossier.montant_devis_174 > 0 && (
                          <span className="text-xs text-muted-foreground">
                            (calculé: {formatCurrency(dossier.montant_devis_174)})
                          </span>
                        )}
                      </div>
                    )}

                    {isDateStep && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" disabled={isLocked || isSaving}
                            className={cn("w-[150px] justify-start text-left font-normal rounded-sm", !dateInputs[step.key] && "text-muted-foreground")}
                            data-testid={`date-picker-${step.key}`}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {dateInputs[step.key] ? format(dateInputs[step.key], "dd/MM/yyyy") : "Sélectionner"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={dateInputs[step.key]} onSelect={(date) => handleDateChange(step.key, date)} locale={fr} initialFocus />
                        </PopoverContent>
                      </Popover>
                    )}
                    
                    {isPaymentStep && (
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input type="number" placeholder="Montant final"
                            value={amountInputs[step.key] || ""}
                            onChange={(e) => handleAmountChange(step.key, e.target.value)}
                            disabled={isLocked}
                            className="w-[140px] pl-9 rounded-sm text-right"
                            data-testid={`amount-input-${step.key}`}
                          />
                        </div>
                        <Button variant="outline" size="sm"
                          onClick={() => handleAmountSave(step.key)}
                          disabled={isSaving || isLocked}
                          className="rounded-sm" data-testid={`save-amount-${step.key}`}
                        >
                          <Save className="w-4 h-4" />
                        </Button>
                      </div>
                    )}

                    <Select value={step.status} onValueChange={(value) => handleStepStatusChangeWithValidation(step.key, value)} disabled={isSaving || isLocked}>
                      <SelectTrigger className={cn("w-[160px] rounded-sm", stepStatusConfig.bg, stepStatusConfig.border, "border")}
                        data-testid={`step-status-select-${step.key}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_started"><div className="flex items-center gap-2"><Circle className="w-4 h-4 text-muted-foreground" />Non démarré</div></SelectItem>
                        <SelectItem value="in_progress"><div className="flex items-center gap-2"><Loader2 className="w-4 h-4 text-blue-600" />En cours</div></SelectItem>
                        <SelectItem value="waiting"><div className="flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" />En attente</div></SelectItem>
                        <SelectItem value="done"><div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" />Terminé</div></SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {isSaving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Popup travaux pour 174 */}
      <DevisPopup174
        open={showDevisPopup}
        onClose={() => { setShowDevisPopup(false); setPendingDevisStepKey(null); }}
        onSave={handleDevisPopupSave}
        nombreLogements={dossier.nombre_logements || 1}
        existingTravaux={dossier.travaux_devis}
        existingCouts={dossier.couts_unitaires_devis}
        isRestricted={isRestricted}
      />
    </div>
  );
}
